/**
 * Dossha Beden Bulucu
 *
 * Öneri, ürünün kendi `custom.beden_bilgisi` tablosu üzerinden üretilir.
 * Tablodaki ölçüler yatık (yarım) ürün ölçüsüdür, vücut ölçüsü değildir —
 * bu yüzden doğrudan karşılaştırılmaz. Köprü, tablodaki "Beden" satırıdır:
 * her kolonun hangi TR numarasına denk geldiğini üretici zaten bildirir.
 *
 *   kullanıcı yanıtları → vücut ölçüsü tahmini (±belirsizlik)
 *                       → TR numarası dağılımı
 *                       → tablodaki kolon olasılıkları
 *                       → kalıp sapmasıyla düzeltme → sonuç + güven
 *
 * Motor (DosshaSizeEngine) saf fonksiyonlardan oluşur; arayüzden bağımsız
 * olarak test edilebilir.
 */
(function (root, factory) {
  const engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else root.DosshaSizeEngine = engine;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Sabitler
   * ------------------------------------------------------------------ */

  // Vücut ölçüsü → TR numarası eşlemesi, Dossha'nın kendi beden rehberinden
  // (tema ayarları: size_guide_ust_rows / alt_rows) türetildi. Genel TR
  // standardı kullanılırsa herkes yarım-bir beden yukarı kayıyor; markanın
  // M'i göğüs 89-93 iken standart 38 beden 88'dir.
  //
  // Her satır [TR, cm]: harf aralıklarının sınır noktaları.
  //   XS 79-84 · S 85-88 · M 89-93 · L 94-100 · XL 101-105   (göğüs)
  // Sınırlar iki aralığın ortasına konur (84/85 → 84.5) ve TR tek sayıya
  // denk gelir; böylece aralığın içi tam olarak o bedene düşer.
  const SIZE_SCALE = {
    bust:  [[33, 79], [35, 84.5], [37, 88.5], [39, 93.5], [41, 100.5], [43, 105]],
    waist: [[33, 62], [35, 67.5], [37, 71.5], [39, 76.5], [41, 82.5], [43, 87]],
    hip:   [[33, 87], [35, 93.5], [37, 100.5], [39, 106.5], [41, 112.5], [43, 119]],
  };

  // Harf bedeninin TR karşılığı. Katalogdaki 185 üründe tutarlı.
  const LETTER_TR = { XS: 34, S: 36, M: 38, L: 40, XL: 42, XXL: 44 };

  // Vücut tipi, aynı kütleyi farklı dağıtır (cm).
  const SHAPE_DELTA = {
    hourglass:  { bust:  1.5, waist: -3.0, hip:  1.5 },
    pear:       { bust: -2.5, waist: -1.5, hip:  3.5 },
    apple:      { bust:  2.5, waist:  4.0, hip: -2.0 },
    rectangle:  { bust: -1.0, waist:  2.0, hip: -1.5 },
  };

  // Boy/kilo tahmininin standart sapması (cm). Mezurayla ölçülen değerlerde çok düşer.
  const SIGMA_ESTIMATED = { bust: 4.2, waist: 4.5, hip: 3.8 };
  const SIGMA_MEASURED  = { bust: 1.0, waist: 1.0, hip: 1.0 };
  const SIGMA_FROM_BRA  = 2.0;

  // EU sütyen kupası → göğüs çevresi ile bant farkı (cm)
  const CUP_DELTA = { A: 13, B: 15, C: 17, D: 19, E: 21, F: 23, G: 25 };
  // Kupa hacminin sporcu sütyeninde bedene etkisi (TR birimi)
  const CUP_TR_ADJUST = { A: -0.5, B: 0, C: 0.5, D: 1.5, E: 2.5, F: 3.5, G: 4.5 };

  // Bolluk payı (ease) = ürün çevresi − o bedene karşılık gelen vücut ölçüsü.
  // Negatif = kompresyon. Kategori medyanları build-ease-baseline.js ile
  // katalogdan üretildi; ürünün kendi kalıbını bu referansa göre konumlandırır.
  const EASE_BASELINE = {
    bra:         { bust: -23.5 },
    ust_fitted:  { bust: -25 },
    ust_relaxed: { bust: -12.7 },
    alt:         { waist: -17.5, hip: -27 },
    tek_parca:   { bust: -25, hip: -29.5, waist: -6 },
  };

  // Mutlak bolluk payına göre kalıp tanımı (çevrede cm). Aktif giyim
  // ürünleri esnemeden ölçüldüğü için değerler doğal olarak negatiftir;
  // eşikler yukarıdaki kategori medyanlarına göre konumlandırıldı.
  const EASE_LABELS = [
    { max: -32, key: 'compression', label: 'yüksek kompresyon' },
    { max: -20, key: 'fitted',      label: 'vücuda oturan' },
    { max: -10, key: 'regular',     label: 'normal kalıp' },
    { max: 5,   key: 'relaxed',     label: 'rahat kalıp' },
    { max: Infinity, key: 'oversized', label: 'oversize kalıp' },
  ];

  // Kategori medyanından bu kadar sapan model, kendi grubunda bol/dar sayılır.
  const REL_EASE_LOOSE = 8;
  const REL_EASE_TIGHT = -8;

  // İkinci olasılık bu eşiğin üstündeyse alternatif beden gösterilir.
  const ALTERNATIVE_AT = 0.18;

  // Kolon olasılığı bu eşiğin altındaysa tek beden göstermek dürüst değil.
  const CONFIDENCE_HIGH = 0.7;
  const CONFIDENCE_USABLE = 0.45;

  /* ------------------------------------------------------------------ *
   * Yardımcılar
   * ------------------------------------------------------------------ */

  /** "XS/S", "XS-S", "xs s" → "XS|S". Tablo başlığıyla varyant değerini eşler. */
  function canonSize(value) {
    return String(value == null ? '' : value)
      .toLocaleUpperCase('tr-TR')
      .replace(/İ/g, 'I')
      .replace(/\s+/g, '')
      .replace(/[/\-–—_+&]+/g, '|')
      .replace(/^\||\|$/g, '');
  }

  /** "26,5" → 26.5; sayı değilse null. */
  function toNumber(value) {
    const s = String(value == null ? '' : value).trim().replace(',', '.');
    return /^-?\d+(\.\d+)?$/.test(s) ? parseFloat(s) : null;
  }

  /** "32-36" → [32,36] · "38" → [38,38] · "38-40-42" → [38,42] */
  function parseBedenCell(cell) {
    const found = String(cell == null ? '' : cell).match(/\d+/g);
    if (!found) return null;
    const nums = found.map(Number);
    return [Math.min.apply(null, nums), Math.max.apply(null, nums)];
  }

  /** Normal dağılım kümülatifi (Abramowitz–Stegun 7.1.26) */
  function normalCdf(x, mu, sigma) {
    if (!(sigma > 0)) return x < mu ? 0 : 1;
    const z = (x - mu) / (sigma * Math.SQRT2);
    const sign = z < 0 ? -1 : 1;
    const a = Math.abs(z);
    const t = 1 / (1 + 0.3275911 * a);
    const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
    const erf = sign * (1 - poly * Math.exp(-a * a));
    return 0.5 * (1 + erf);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /** Komşu kolon ortalarının tipik TR mesafesi. Tek harfli tablolarda 2,
   *  ikili bedende ("XS/S") çok daha geniş olur. */
  function typicalStep(orderedColumns) {
    if (orderedColumns.length < 2) return 2;
    const mids = orderedColumns.map(function (c) { return (c.tr[0] + c.tr[1]) / 2; });
    const gaps = [];
    for (let i = 1; i < mids.length; i++) gaps.push(mids[i] - mids[i - 1]);
    gaps.sort(function (a, b) { return a - b; });
    return gaps[Math.floor(gaps.length / 2)] || 2;
  }

  // SIZE_SCALE üzerinde parçalı doğrusal ara değer; aralık dışında en yakın
  // bandın eğimiyle uzatılır.
  function interpolate(points, value, fromIdx, toIdx) {
    const last = points.length - 1;
    for (let i = 0; i < last; i++) {
      const lo = points[i];
      const hi = points[i + 1];
      const span = hi[fromIdx] - lo[fromIdx];
      if (value <= hi[fromIdx] || i === last - 1) {
        return lo[toIdx] + ((value - lo[fromIdx]) / span) * (hi[toIdx] - lo[toIdx]);
      }
    }
    return points[0][toIdx];
  }

  function bodyToTr(measure, cm) {
    return interpolate(SIZE_SCALE[measure], cm, 1, 0);
  }

  function trToBody(measure, tr) {
    return interpolate(SIZE_SCALE[measure], tr, 0, 1);
  }

  // Kullanıcının bulunduğu banttaki yerel eğim (kaç cm = 1 TR birimi).
  function cmPerTrAt(measure, cm) {
    const tr = bodyToTr(measure, cm);
    return (trToBody(measure, tr + 1) - trToBody(measure, tr - 1)) / 2;
  }

  /* ------------------------------------------------------------------ *
   * 1) Yanıtlardan vücut ölçüsü tahmini
   * ------------------------------------------------------------------ */

  /**
   * @param {Object} answers height, weight, bodyShape, bust?, waist?, hip?, braBand?, braCup?
   * @returns {{bust:number,waist:number,hip:number,sigma:Object,source:Object}}
   */
  function estimateBody(answers) {
    const height = Number(answers.height) || 165;
    const weight = Number(answers.weight) || 60;
    const bmi = weight / Math.pow(height / 100, 2);

    // Aynı BMI'da uzun boylu birinin çevre ölçüleri bir miktar büyüktür.
    const heightScale = 1 + (height - 165) * 0.0018;

    // Katsayılar, Dossha beden rehberindeki XS-XL vücut ölçülerine (165 cm
    // referans boy) en küçük kareler ile oturtuldu. Marka basen-bel farkını
    // ~29 cm varsayıyor; genel formüller ~23 cm verip basenden beden
    // küçültüyordu.
    const body = {
      bust:  (81.9 + (bmi - 18.5) * 2.00) * heightScale,
      waist: (65.1 + (bmi - 18.5) * 1.91) * heightScale,
      hip:   (91.3 + (bmi - 18.5) * 2.40) * heightScale,
    };

    const delta = SHAPE_DELTA[answers.bodyShape];
    if (delta) {
      body.bust += delta.bust;
      body.waist += delta.waist;
      body.hip += delta.hip;
    }

    const sigma = Object.assign({}, SIGMA_ESTIMATED);
    const source = { bust: 'estimated', waist: 'estimated', hip: 'estimated' };

    // Sütyen bedeni, göğüs tahminini belirgin şekilde daraltır.
    const band = toNumber(answers.braBand);
    const cup = answers.braCup ? String(answers.braCup).toUpperCase() : null;
    if (band && cup && CUP_DELTA[cup] != null) {
      body.bust = band + CUP_DELTA[cup];
      body.band = band;
      body.cup = cup;
      sigma.bust = SIGMA_FROM_BRA;
      source.bust = 'bra';
    }

    // Mezura ölçüsü her şeyin önündedir.
    ['bust', 'waist', 'hip'].forEach(function (key) {
      const measured = toNumber(answers[key]);
      if (measured) {
        body[key] = measured;
        sigma[key] = SIGMA_MEASURED[key];
        source[key] = 'measured';
      }
    });

    body.bmi = bmi;
    return { body: body, sigma: sigma, source: source };
  }

  /* ------------------------------------------------------------------ *
   * 2) Ürün tablosunu çözümle
   * ------------------------------------------------------------------ */

  /**
   * beden_bilgisi metafield'ını kolon kolon yapıya çevirir.
   * @returns {{columns:Array, measures:Array, hasBedenRow:boolean}|null}
   */
  function parseChart(chart) {
    if (!chart || !Array.isArray(chart.headers) || !Array.isArray(chart.rows)) return null;

    const labels = chart.headers.slice(1).map(function (h) { return String(h).trim(); });
    if (!labels.length) return null;

    let bedenRow = null;
    const measureRows = [];
    chart.rows.forEach(function (row) {
      if (!Array.isArray(row) || !row.length) return;
      const label = String(row[0]).trim();
      if (label === 'Beden') bedenRow = row;
      else measureRows.push({ label: label, cells: row.slice(1) });
    });

    const columns = labels.map(function (label, i) {
      const col = {
        index: i,
        label: label,
        canon: canonSize(label),
        tr: bedenRow ? parseBedenCell(bedenRow[i + 1]) : null,
        measures: {},
      };
      measureRows.forEach(function (mr) {
        const v = toNumber(mr.cells[i]);
        if (v !== null) col.measures[mr.label] = v;
      });
      return col;
    });

    // "Beden" satırı yoksa harf bedeninden TR aralığı türet (XS=34 … XL=42).
    if (!bedenRow) {
      const FALLBACK = { XS: [34, 34], S: [36, 36], M: [38, 38], L: [40, 40], XL: [42, 42], XXL: [44, 44] };
      columns.forEach(function (col) {
        const parts = col.canon.split('|');
        const lo = FALLBACK[parts[0]];
        const hi = FALLBACK[parts[parts.length - 1]];
        if (lo && hi) col.tr = [lo[0], hi[1]];
      });
    }

    if (columns.some(function (c) { return !c.tr; })) return null;

    return {
      columns: columns,
      measures: measureRows.map(function (r) { return r.label; }),
      hasBedenRow: !!bedenRow,
    };
  }

  /* ------------------------------------------------------------------ *
   * 3) Hangi ölçü bu üründe belirleyici
   * ------------------------------------------------------------------ */

  const MEASURE_KEYS = {
    bust: ['Göğüs (cm)', 'Göğüs'],
    waist: ['Bel (cm)', 'Bel'],
    hip: ['Basen (cm)', 'Basen'],
  };

  function chartMeasureValue(column, measure) {
    const keys = MEASURE_KEYS[measure] || [];
    for (let i = 0; i < keys.length; i++) {
      if (column.measures[keys[i]] != null) return column.measures[keys[i]];
    }
    return null;
  }

  /**
   * "M/L" gibi birleşik bir kolonda kişinin gerçekte hangi harf olduğunu
   * söyler. Varyant tek parça satıldığı için satın alınacak beden yine
   * "M/L"dir; ama kullanıcıya "sen M'sin" demek, iki bedenden hangisi
   * olduğunu bilmek isteyen kişi için tek anlamlı cevaptır.
   *
   * @returns {{letter:string, letters:string[]}|null} tek harfli kolonda null
   */
  function resolveLetter(column, muTr) {
    const letters = column.canon.split('|').filter(function (l) { return LETTER_TR[l] != null; });
    if (letters.length < 2) return null;

    let best = letters[0];
    let bestDist = Infinity;
    letters.forEach(function (l) {
      const d = Math.abs(LETTER_TR[l] - muTr);
      // Eşitlikte küçük harf kazanır: sırayla gezildiği için > yeterli.
      if (d < bestDist) { bestDist = d; best = l; }
    });
    return { letter: best, letters: letters };
  }

  /**
   * Kategori ve tablonun içerdiği satırlar birlikte, hangi vücut ölçüsünün
   * ne ağırlıkta rol oynayacağını belirler.
   */
  function measureWeights(category, parsed) {
    const present = {};
    ['bust', 'waist', 'hip'].forEach(function (m) {
      present[m] = parsed.columns.some(function (c) { return chartMeasureValue(c, m) !== null; });
    });

    let weights;
    switch (category) {
      case 'bra':         weights = { bust: 1.00, waist: 0.00, hip: 0.00 }; break;
      case 'ust_fitted':  weights = { bust: 0.75, waist: 0.25, hip: 0.00 }; break;
      case 'ust_relaxed': weights = { bust: 0.85, waist: 0.15, hip: 0.00 }; break;
      case 'alt':         weights = { bust: 0.00, waist: 0.35, hip: 0.65 }; break;
      case 'tek_parca':   weights = { bust: 0.45, waist: 0.15, hip: 0.40 }; break;
      default:            weights = { bust: 0.50, waist: 0.20, hip: 0.30 };
    }

    // Tabloda karşılığı olmayan ölçünün ağırlığı kalanlara dağıtılır.
    let total = 0;
    ['bust', 'waist', 'hip'].forEach(function (m) {
      if (!present[m]) weights[m] = 0;
      total += weights[m];
    });
    if (total <= 0) {
      // Tabloda hiç çevre ölçüsü yok (ör. yalnızca uzunluk) → kategoriye düş.
      weights = category === 'alt' ? { bust: 0, waist: 0.4, hip: 0.6 } : { bust: 1, waist: 0, hip: 0 };
      total = 1;
    }
    ['bust', 'waist', 'hip'].forEach(function (m) { weights[m] /= total; });
    return weights;
  }

  /**
   * Bu üründe sonucu gerçekten belirleyen ölçüler. Arayüz yalnızca bunları
   * zorunlu tutar: bir üst için basen sormak gereksiz sürtünme yaratır,
   * ağırlığı sıfır olduğu için sonuca da etki etmez.
   *
   * @param {Object} product { chart, category }
   * @returns {string[]} 'bust' | 'waist' | 'hip'
   */
  function requiredMeasures(product) {
    const parsed = parseChart(product && product.chart);
    if (!parsed) return [];
    const weights = measureWeights(product.category || 'ust_fitted', parsed);
    return ['bust', 'waist', 'hip'].filter(function (m) { return weights[m] > 0; });
  }

  /* ------------------------------------------------------------------ *
   * 4) Kolon olasılıkları
   * ------------------------------------------------------------------ */

  /**
   * Kullanıcının TR numarası bir dağılım olduğu için, her kolona düşme
   * olasılığı hesaplanır. Böylece "iki beden arasında" durumu kendiliğinden
   * ortaya çıkar; uydurma bir eşiğe gerek kalmaz.
   */
  function columnProbabilities(columns, muTr, sigmaTr) {
    const sorted = columns.slice().sort(function (a, b) { return a.tr[0] - b.tr[0]; });

    const bounds = sorted.map(function (col, i) {
      const prev = sorted[i - 1];
      const next = sorted[i + 1];
      return {
        col: col,
        lower: prev ? (prev.tr[1] + col.tr[0]) / 2 : -Infinity,
        upper: next ? (col.tr[1] + next.tr[0]) / 2 : Infinity,
      };
    });

    const probs = bounds.map(function (b) {
      return {
        column: b.col,
        p: normalCdf(b.upper, muTr, sigmaTr) - normalCdf(b.lower, muTr, sigmaTr),
      };
    });

    const sum = probs.reduce(function (acc, x) { return acc + x.p; }, 0) || 1;
    probs.forEach(function (x) { x.p /= sum; });
    probs.sort(function (a, b) { return b.p - a.p; });
    return probs;
  }

  /* ------------------------------------------------------------------ *
   * 5) Kalıp sapması — bu model kategorisine göre bol mu, dar mı
   * ------------------------------------------------------------------ */

  /**
   * Seçilen kolonun bolluk payını çıkarır.
   *
   *   ease = ürün çevresi (yatık × 2) − kolonun TR numarasına karşılık gelen
   *          vücut ölçüsü
   *
   * Mutlak değer kalıbı tarif eder (oversize / vücuda oturan). Kategori
   * medyanına göre sapma ise kesim tercihinin ne kadar etkili olacağını
   * belirler. Hiçbiri bedeni doğrudan kaydırmaz — üretici "Beden" satırında
   * niyetini zaten bildirmiştir.
   */
  function fitAnalysis(column, category, primaryMeasure) {
    const baseline = EASE_BASELINE[category] || {};
    const order = [primaryMeasure, 'bust', 'hip', 'waist'];

    for (let i = 0; i < order.length; i++) {
      const measure = order[i];
      if (!measure) continue;
      const flat = chartMeasureValue(column, measure);
      if (flat === null) continue;

      const midTr = (column.tr[0] + column.tr[1]) / 2;
      const bodyCm = trToBody(measure, midTr);
      const ease = flat * 2 - bodyCm;

      const base = baseline[measure];
      const relative = base == null ? null : ease - base;

      let label = EASE_LABELS[EASE_LABELS.length - 1];
      for (let j = 0; j < EASE_LABELS.length; j++) {
        if (ease <= EASE_LABELS[j].max) { label = EASE_LABELS[j]; break; }
      }

      return {
        measure: measure,
        flat: flat,
        bodyCm: Math.round(bodyCm * 10) / 10,
        ease: Math.round(ease * 10) / 10,
        relative: relative === null ? null : Math.round(relative * 10) / 10,
        key: label.key,
        label: label.label,
        runsLoose: relative !== null && relative >= REL_EASE_LOOSE,
        runsTight: relative !== null && relative <= REL_EASE_TIGHT,
      };
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * 6) Ana hesap
   * ------------------------------------------------------------------ */

  /**
   * @param {Object} answers  kullanıcı yanıtları
   * @param {Object} product  { chart, category, variants:[{size,available}] }
   * @returns {Object} öneri
   */
  function recommend(answers, product) {
    const parsed = parseChart(product.chart);
    if (!parsed) {
      return { ok: false, reason: 'no_chart', message: 'Bu ürün için beden tablosu bulunamadı.' };
    }

    const category = product.category || 'ust_fitted';
    const est = estimateBody(answers);
    const weights = measureWeights(category, parsed);

    // Her ölçünün kendi TR karşılığı
    const perMeasure = {};
    ['bust', 'waist', 'hip'].forEach(function (m) {
      if (weights[m] <= 0) return;
      perMeasure[m] = {
        tr: bodyToTr(m, est.body[m]),
        sigmaTr: est.sigma[m] / cmPerTrAt(m, est.body[m]),
        weight: weights[m],
        cm: Math.round(est.body[m] * 10) / 10,
        source: est.source[m],
      };
    });

    // Sütyen kupası, sporcu sütyeninde hacim olarak bedene yansır.
    if (category === 'bra' && est.body.cup && CUP_TR_ADJUST[est.body.cup] != null && perMeasure.bust) {
      perMeasure.bust.tr += CUP_TR_ADJUST[est.body.cup];
      perMeasure.bust.cupAdjusted = true;
    }

    const keys = Object.keys(perMeasure);
    if (!keys.length) {
      return { ok: false, reason: 'no_measure', message: 'Bu ürün için belirleyici ölçü çıkarılamadı.' };
    }

    // Tahmine dayalı öneri istenmiyorsa, belirleyici ölçülerin hepsi gerçekten
    // ölçülmüş olmalı. Sütyende bant + kupa ölçünün kendisidir.
    if (product.requireMeasured) {
      const missing = keys.filter(function (m) {
        return est.source[m] !== 'measured' && est.source[m] !== 'bra';
      });
      if (missing.length) {
        return {
          ok: false,
          reason: 'needs_measurements',
          missing: missing,
          message: 'Beden önerebilmek için ölçülerini girmen gerekiyor.',
        };
      }
    }

    // Ağırlıklı ortalama TR
    let muTr = 0;
    keys.forEach(function (m) { muTr += perMeasure[m].weight * perMeasure[m].tr; });

    // Belirsizlik iki kaynaktan gelir: ölçüm hatası ve ölçülerin birbiriyle
    // çelişmesi (ör. göğsü S, kalçası L). İkincisi ağırlıklı varyansla
    // hesaplanır ki düşük ağırlıklı bir ölçü sonucu tek başına bulandırmasın.
    let measurementVar = 0;
    let spreadVar = 0;
    keys.forEach(function (m) {
      const pm = perMeasure[m];
      measurementVar += Math.pow(pm.weight * pm.sigmaTr, 2);
      spreadVar += pm.weight * Math.pow(pm.tr - muTr, 2);
    });

    const trs = keys.map(function (m) { return perMeasure[m].tr; });
    const disagreement = keys.length > 1
      ? Math.max.apply(null, trs) - Math.min.apply(null, trs)
      : 0;

    let sigmaTr = clamp(Math.sqrt(measurementVar + spreadVar), 0.35, 6);

    const probs = columnProbabilities(parsed.columns, muTr, sigmaTr);
    const top = probs[0];
    const second = probs[1] || null;

    // Belirleyici ölçü: en yüksek ağırlıklı olan
    const primaryMeasure = keys.reduce(function (best, m) {
      return perMeasure[m].weight > perMeasure[best].weight ? m : best;
    }, keys[0]);

    const fit = fitAnalysis(top.column, category, primaryMeasure);

    // Kesim tercihi. Zaten kendi grubunun çok üstünde bol olan bir üründe
    // "bol olsun" bedeni büyütmez; zaten dar kalan üründe "vücuda otursun"
    // daha da daraltmaz.
    let fitShift = Number(answers.fitShift) || 0;
    let fitCapped = null;
    if (fit) {
      if (fitShift > 0 && fit.runsLoose) { fitShift = 0; fitCapped = 'already_loose'; }
      if (fitShift < 0 && fit.runsTight) { fitShift = 0; fitCapped = 'already_tight'; }
    }

    const ordered = parsed.columns.slice().sort(function (a, b) { return a.tr[0] - b.tr[0]; });
    const topIdx = ordered.indexOf(top.column);
    const primaryIdx = fitShift ? clamp(topIdx + fitShift, 0, ordered.length - 1) : topIdx;
    const primary = ordered[primaryIdx];

    // Kalıp uyarısı: kolonun neresinde durduğuna bakılır. Kendi grubuna göre
    // dar kalan bir üründe aralığın üst ucundaysan, bir üst beden gerçekten
    // daha iyi oturur.
    let sizingHint = null;
    if (fit && primary === top.column) {
      const mid = (top.column.tr[0] + top.column.tr[1]) / 2;
      if (fit.runsTight && muTr > mid && topIdx < ordered.length - 1) sizingHint = 'consider_up';
      else if (fit.runsLoose && muTr < mid && topIdx > 0) sizingHint = 'consider_down';
    }

    // Alternatif beden: kesim tercihi bedeni kaydırdıysa asıl hesap sonucu,
    // kalıp uyarısı varsa işaret edilen beden, aksi halde ikinci olasılık.
    let alternative = null;
    if (fitShift && primary !== top.column) alternative = top.column;
    else if (sizingHint === 'consider_up') alternative = ordered[topIdx + 1];
    else if (sizingHint === 'consider_down') alternative = ordered[topIdx - 1];
    else if (second && second.p >= ALTERNATIVE_AT) alternative = second.column;

    // Skalanın dışında kalan kullanıcıya "XL" demek garantili hatadır; en uçtaki
    // bedene yapıştığımızı açıkça söylememiz gerekir. Uç kolonlar, tabloda
    // yazan aralığın yarım adım ötesine kadar kişiyi taşıyabilir.
    const lowest = ordered[0];
    const highest = ordered[ordered.length - 1];
    const halfStep = typicalStep(ordered) / 2;
    let outOfRange = null;
    if (muTr > highest.tr[1] + halfStep + 0.5) outOfRange = 'above';
    else if (muTr < lowest.tr[0] - halfStep - 0.5) outOfRange = 'below';

    const confidence = top.p;
    let level = confidence >= CONFIDENCE_HIGH ? 'high' : (confidence >= CONFIDENCE_USABLE ? 'medium' : 'low');
    if (outOfRange) level = 'out_of_range';

    const stock = matchStock(product.variants, [primary, alternative]);
    const resolved = resolveLetter(primary, muTr);

    return {
      ok: true,
      primary: primary.label,
      primaryCanon: primary.canon,
      // Birleşik bedenlerde kullanıcıya gösterilecek tek harf; varyant adı
      // (primary) satın alma için olduğu gibi korunur.
      primaryLetter: resolved ? resolved.letter : primary.label,
      combinedWith: resolved ? resolved.letters.filter(function (l) { return l !== resolved.letter; }) : [],
      alternative: alternative ? alternative.label : null,
      alternativeCanon: alternative ? alternative.canon : null,
      alternativeLetter: alternative
        ? ((resolveLetter(alternative, muTr) || {}).letter || alternative.label)
        : null,
      confidence: Math.round(confidence * 100) / 100,
      level: level,
      needsMoreInfo: level === 'low',
      outOfRange: outOfRange,
      trEstimate: Math.round(muTr * 10) / 10,
      sigmaTr: Math.round(sigmaTr * 100) / 100,
      disagreement: Math.round(disagreement * 10) / 10,
      body: {
        bust: Math.round(est.body.bust * 10) / 10,
        waist: Math.round(est.body.waist * 10) / 10,
        hip: Math.round(est.body.hip * 10) / 10,
      },
      sources: est.source,
      perMeasure: perMeasure,
      weights: weights,
      primaryMeasure: primaryMeasure,
      fit: fit,
      fitShift: fitShift,
      fitCapped: fitCapped,
      sizingHint: sizingHint,
      stock: stock,
      probabilities: probs.map(function (x) { return { size: x.column.label, p: Math.round(x.p * 100) / 100 }; }),
      category: category,
    };
  }

  /** Tablo başlığını gerçek varyantla eşler (XS/S ↔ XS-S). */
  function matchStock(variants, columns) {
    const out = {};
    if (!Array.isArray(variants)) return out;
    columns.forEach(function (col) {
      if (!col) return;
      const target = col.canon;
      const hit = variants.find(function (v) { return canonSize(v.size) === target; });
      out[col.label] = {
        found: !!hit,
        available: hit ? !!hit.available : false,
        variantId: hit ? hit.id : null,
      };
    });
    return out;
  }

  return {
    recommend: recommend,
    estimateBody: estimateBody,
    parseChart: parseChart,
    canonSize: canonSize,
    columnProbabilities: columnProbabilities,
    measureWeights: measureWeights,
    requiredMeasures: requiredMeasures,
    fitAnalysis: fitAnalysis,
    bodyToTr: bodyToTr,
    trToBody: trToBody,
    constants: {
      SIZE_SCALE: SIZE_SCALE,
      LETTER_TR: LETTER_TR,
      SHAPE_DELTA: SHAPE_DELTA,
      EASE_BASELINE: EASE_BASELINE,
      CUP_DELTA: CUP_DELTA,
      CONFIDENCE_HIGH: CONFIDENCE_HIGH,
      CONFIDENCE_USABLE: CONFIDENCE_USABLE,
    },
  };
});
